/* SATS: correct Shopify price sorting to use the customer-facing £/m² value. */
(() => {
  const PRICE_SORTS = new Set(['price-ascending', 'price-descending']);
  const sourceSection = () => window.location.pathname.endsWith('/search') ? 'sats-search-sort-page' : 'sats-m2-sort-page';
  let runToken = 0;
  let scheduleTimer = null;

  const currentSort = () => new URLSearchParams(window.location.search).get('sort_by') || '';
  const needsSatsSort = () => window.location.pathname.includes('/collections/') || window.location.pathname.endsWith('/search');

  function scheduleSort() {
    clearTimeout(scheduleTimer);
    scheduleTimer = setTimeout(applySatsM2Sort, 0);
  }

  function buildSectionUrl(sectionId, page) {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('page', String(page));
    return `${url.pathname}?${url.searchParams.toString()}`;
  }

  async function fetchDocument(sectionId, page) {
    const response = await fetch(buildSectionUrl(sectionId, page), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal: AbortSignal.timeout(15000),
    });
    if (!response.ok) throw new Error(`SATS £/m² sort fetch failed: ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  async function resolveVisiblePageSize(grid, currentPage, token) {
    const configuredSize = Number(grid.dataset.satsPageSize || grid.closest('[data-sats-page-size]')?.dataset.satsPageSize);
    if (configuredSize > 0) return configuredSize;
    const existingCount = grid.querySelectorAll(':scope > .grid__item').length;
    if (currentPage <= 1 || existingCount === 0) return existingCount || 12;

    const configuredSectionId = grid.dataset.id;
    if (!configuredSectionId) return 12;

    try {
      const pageOneDocument = await fetchDocument(configuredSectionId, 1);
      if (token !== runToken) return 12;
      const pageOneGrid = pageOneDocument.getElementById('product-grid');
      const count = pageOneGrid ? pageOneGrid.querySelectorAll(':scope > .grid__item').length : 0;
      return count || 12;
    } catch (error) {
      console.warn(error);
      return 12;
    }
  }

  function appendCmWhereRequired(grid) {
    grid.querySelectorAll('[data-sats-append-cm="true"]').forEach((card) => {
      card.querySelectorAll('.card__heading .full-unstyled-link').forEach((link) => {
        const text = link.textContent.trim();
        if (text && !/\s+cm$/i.test(text)) link.append(document.createTextNode(' cm'));
      });
    });
  }

  function sortCards(cards, direction) {
    return cards
      .map((card, originalIndex) => ({
        card,
        originalIndex,
        price: Number(card.dataset.satsM2Price),
        available: card.dataset.satsAvailable === 'true',
      }))
      .sort((a, b) => {
        if (a.available !== b.available) return a.available ? -1 : 1;
        if (!PRICE_SORTS.has(currentSort())) return a.originalIndex - b.originalIndex;
        const aValid = Number.isFinite(a.price) && a.price > 0;
        const bValid = Number.isFinite(b.price) && b.price > 0;
        if (!aValid && !bValid) return a.originalIndex - b.originalIndex;
        if (!aValid) return 1;
        if (!bValid) return -1;
        const difference = direction === 'asc' ? a.price - b.price : b.price - a.price;
        return difference || a.originalIndex - b.originalIndex;
      })
      .map(({ card }) => card);
  }

  async function applySatsM2Sort() {
    if (!needsSatsSort()) return;

    const token = ++runToken;
    const root = document.getElementById('product-grid');
    const grid = root?.matches('ul.product-grid') ? root : root?.querySelector('ul.product-grid');
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    if (!grid || !grid.children.length) {
      container.classList.remove('sats-m2-sort-loading');
      return;
    }

    const signature = `${window.location.pathname}${window.location.search}`;
    if (grid.dataset.satsM2SortedFor === signature) return;

    const params = new URLSearchParams(window.location.search);
    const currentPage = Math.max(1, Number(params.get('page')) || 1);
    const direction = currentSort() === 'price-descending' ? 'desc' : 'asc';

    container.classList.add('sats-m2-sort-loading');

    try {
      const [sourcePageOne, pageSize] = await Promise.all([
        fetchDocument(sourceSection(), 1),
        resolveVisiblePageSize(grid, currentPage, token),
      ]);
      if (token !== runToken) return;

      const sourceRoot = sourcePageOne.querySelector('[data-sats-m2-source]');
      if (!sourceRoot) throw new Error('SATS £/m² sort source section was not returned.');

      const totalPages = Math.max(1, Number(sourceRoot.dataset.totalPages) || 1);
      const documents = [sourcePageOne];

      if (totalPages > 1) {
        const remaining = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, index) => fetchDocument(sourceSection(), index + 2))
        );
        documents.push(...remaining);
      }
      if (token !== runToken) return;

      const seen = new Set();
      const cards = [];
      documents.forEach((document) => {
        document.querySelectorAll('[data-sats-m2-card]').forEach((card) => {
          const id = card.dataset.satsProductId || '';
          if (id && seen.has(id)) return;
          if (id) seen.add(id);
          cards.push(card);
        });
      });

      if (cards.length !== Number(sourceRoot.dataset.totalProducts)) throw new Error('Incomplete SATS sort results');
      if (!grid.isConnected || signature !== `${window.location.pathname}${window.location.search}`) return;
      const sorted = sortCards(cards, direction);
      const start = (currentPage - 1) * pageSize;
      const selected = sorted.slice(start, start + pageSize);

      const fragment = document.createDocumentFragment();
      selected.forEach((card) => fragment.appendChild(document.importNode(card, true)));
      grid.replaceChildren(fragment);
      appendCmWhereRequired(grid);
      grid.dataset.satsM2SortedFor = signature;
    } catch (error) {
      console.error(error);
      // Fail safely: leave Shopify's native result in place if the custom sort cannot load.
    } finally {
      if (token === runToken) container.classList.remove('sats-m2-sort-loading');
    }
  }

  const observer = new MutationObserver((mutations) => {
    if (!needsSatsSort()) return;
    const grid = document.getElementById('product-grid');
    if (!grid) return;

    const relevant = mutations.some((mutation) => {
      if (mutation.target === grid || mutation.target.id === 'ProductGridContainer') return true;
      return Array.from(mutation.addedNodes).some(
        (node) => node.nodeType === 1 && (node.id === 'ProductGridContainer' || node.querySelector?.('#ProductGridContainer'))
      );
    });
    if (relevant) scheduleSort();
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSort, { once: true });
  } else {
    scheduleSort();
  }
})();

